import { useEffect, useState } from "react";
import { BLOG_ROUTES } from "./routes";
import type { BlogPost, BlogPostSummary, BlogRouteId } from "./types";

/* Globs instead of plain imports: the build works (with an empty blog) even
   before scripts/fetch-blog.mjs has written anything. */
const indexFiles = import.meta.glob<BlogPostSummary[]>("../generated/blog/index.json", {
  eager: true,
  import: "default",
});
const postFiles = import.meta.glob<BlogPost>("../generated/blog/posts/*.json", { import: "default" });

/** Every published article, newest first (including hidden-from-Google ones). */
export const ALL_BLOG_POSTS: readonly BlogPostSummary[] = Object.values(indexFiles)[0] ?? [];

/** Articles shown in listings: test articles hidden from Google stay out. */
export const BLOG_POSTS: readonly BlogPostSummary[] = ALL_BLOG_POSTS.filter((post) => !post.noindex);

export function getBlogSummary(slug: string): BlogPostSummary | undefined {
  return ALL_BLOG_POSTS.find((post) => post.slug === slug);
}

/** A route's articles in reading order: numbered steps first, then by date. */
export function postsForRoute(route: BlogRouteId): BlogPostSummary[] {
  return BLOG_POSTS.filter((post) => post.route === route).sort((a, b) => {
    const stepA = a.routeStep ?? Number.MAX_SAFE_INTEGER;
    const stepB = b.routeStep ?? Number.MAX_SAFE_INTEGER;
    if (stepA !== stepB) return stepA - stepB;
    return a.publishedAt.localeCompare(b.publishedAt);
  });
}

/** Routes that have at least one article (only those get a page). */
export function activeBlogRoutes() {
  return BLOG_ROUTES.filter((route) => BLOG_POSTS.some((post) => post.route === route.id));
}

/** Paths prerendered for the blog. */
export function blogPrerenderPaths(): string[] {
  return [
    "/blog",
    ...activeBlogRoutes().map((route) => `/blog/ruta/${route.id}`),
    ...ALL_BLOG_POSTS.map((post) => `/blog/${post.slug}`),
  ];
}

/* Full articles load on demand (one chunk each). The server fills the cache
   up front; the browser loads the current article before hydrating. */
const cache = new Map<string, BlogPost>();

export function primeBlogPosts(posts: BlogPost[]) {
  for (const post of posts) cache.set(post.slug, post);
}

export function getCachedBlogPost(slug: string): BlogPost | undefined {
  return cache.get(slug);
}

export async function loadBlogPost(slug: string): Promise<BlogPost | null> {
  const cached = cache.get(slug);
  if (cached) return cached;
  const loader = postFiles[`../generated/blog/posts/${slug}.json`];
  if (!loader) return null;
  const post = await loader();
  cache.set(slug, post);
  return post;
}

/** Slug of the article at `pathname`, if it is one. */
export function blogSlugFromPath(pathname: string): string | null {
  const match = /^\/blog\/([a-z0-9-]+)\/?$/.exec(pathname);
  return match && match[1] !== "ruta" ? match[1] : null;
}

export function useBlogPost(slug: string): { post: BlogPost | null; loading: boolean } {
  const [state, setState] = useState(() => ({ slug, post: getCachedBlogPost(slug) ?? null }));
  const current = state.slug === slug ? state.post : (getCachedBlogPost(slug) ?? null);
  const known = Boolean(getBlogSummary(slug));

  useEffect(() => {
    if (getCachedBlogPost(slug)) {
      setState({ slug, post: getCachedBlogPost(slug) ?? null });
      return;
    }
    let active = true;
    void loadBlogPost(slug).then((post) => {
      if (active) setState({ slug, post });
    });
    return () => {
      active = false;
    };
  }, [slug]);

  return { post: current, loading: known && !current };
}
